<template>
  <div class="projects">
    <OmxList
        :title="$t('top-menu.projects')"
        :data="filteredProjectsData"
        :loading="isLoading"
        :search-value.sync="search"
        :empty-icon="hasActiveFilters ? 'filter-variant' : 'view-grid-outline'"
        :empty-title="hasActiveFilters ? $t('projects-empty.filtered-title') : $t('projects-empty.title')"
        :empty-body="hasActiveFilters ? $t('projects-empty.filtered-body') : $t('projects-empty.body')"
        :skeleton-columns="[1, 1.4, 1.4, 1.4, 1, 1.6]"
    >
      <template #actions>
        <b-dropdown
            v-if="currentUserAccess('Projects', 'CREATE')"
            :triggers="['hover']"
            position="is-bottom-left"
            aria-role="list"
        >
          <template #trigger>
            <b-button type="is-primary" icon-left="plus-thick">
              {{ $t('projects-view.new-project-button') }}
            </b-button>
          </template>
          <b-dropdown-item aria-role="listitem" @click="createNewProjectClicked">
            {{ $t('projects-view.create-new-project') }}
          </b-dropdown-item>
          <b-dropdown-item aria-role="listitem" @click="createFromTemplateClicked">
            {{ $t('projects-view.create-from-template') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <template #chips>
        <b-select v-model="selectedPhase"
                  size="is-small"
                  :placeholder="$t('project-details-view.project-status-placeholder')">
          <option v-for="option in projectsPhases" :value="option.id" :key="option.id">
            {{ $t(option.translationKey) }}
          </option>
        </b-select>
        <b-switch v-model="isOnlyArchived" size="is-small">
          {{ $t('projects-view.show-archived-projects-filter') }}
        </b-switch>
      </template>

      <template #empty-actions>
        <OmxPermissionButton
            v-if="!hasActiveFilters"
            :allowed="currentUserAccess('Projects', 'CREATE')"
            type="is-primary"
            icon-left="plus-thick"
            @click="createNewProjectClicked"
        >{{ $t('projects-view.create-new-project') }}</OmxPermissionButton>
        <b-button v-if="hasActiveFilters" @click="resetFilters">
          {{ $t('omx-list.clear-selection') }}
        </b-button>
      </template>

      <!-- columns: passed through to the inner b-table -->
      <b-table-column field="project.code" :label="$t('projects-view.project-code-column')" v-slot="props" sortable>
        <a :href="projectDetailsLink(props.row.project.document_id)" class="omx-link">
          {{ props.row.project.code }}
        </a>
      </b-table-column>

      <b-table-column field="project.title" :label="$t('projects-view.project-title-column')" v-slot="props" sortable>
        <span class="has-text-weight-semibold">{{ props.row.project.title }}</span>
      </b-table-column>

      <b-table-column field="manager_names" :label="$t('projects-view.project-managers-column')" v-slot="props">
        <b-taglist>
          <b-tag type="is-info is-light" size="is-small"
                 v-for="projectManager in props.row.project.meta.managers"
                 v-bind:key="projectManager.name.first">
            {{ getFullName(projectManager.name) }}
          </b-tag>
        </b-taglist>
      </b-table-column>

      <b-table-column field="site_manager_names" :label="$t('projects-view.site-managers-column')" v-slot="props">
        <b-taglist>
          <b-tag type="is-info is-light" size="is-small"
                 v-for="siteManager in props.row.project.meta.site_managers"
                 v-bind:key="siteManager.name.first">
            {{ getFullName(siteManager.name) }}
          </b-tag>
        </b-taglist>
      </b-table-column>

      <b-table-column field="project.phase" :label="$t('projects-view.status-column')" v-slot="props" sortable>
        {{ phaseLabel(props.row.project.phase) }}
      </b-table-column>

      <b-table-column :visible="isOnlyArchived" field="is_archived"
                      :label="$t('projects-view.archived-column')" v-slot="props"
                      centered>
        <b-icon v-if="props.row.project.is_archived" icon="check" type="is-primary" />
      </b-table-column>

      <b-table-column v-slot="props" cell-class="has-text-right">
        <b-button
            v-if="currentUserAccess('ProjectSummary', 'VIEW', props.row.project.document_id)"
            type="is-primary" size="is-small" class="m-1"
            tag="router-link"
            :to="projectSummaryLink(props.row.project.document_id)"
        >{{ $t('projects-view.show-summary') }}</b-button>
        <b-button
            v-if="currentUserAccess('ProjectLogs', 'VIEW', props.row.project.document_id)"
            type="is-primary" size="is-small" class="m-1"
            tag="router-link"
            :to="projectWorklogsLink(props.row.project.document_id)"
        >{{ $t('projects-view.enter-worklog') }}</b-button>
      </b-table-column>
    </OmxList>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "Projects",
  components: { OmxList },
  data() {
    return {
      projectsData: [],
      userRole: {},
      isLoading: false,
      isOnlyArchived: false,
      selectedPhase: "all",
      search: ""
    };
  },
  watch: {
    isOnlyArchived() { this.readProjectsData(); },
    selectedPhase() { this.readProjectsData(); }
  },
  computed: {
    projectsPhases() {
      const allProjectPhases = this.projectPhaseOptions.slice();
      allProjectPhases.unshift({ id: "all", translationKey: "enums.project-phase.all" });
      return allProjectPhases;
    },
    hasActiveFilters() {
      return this.search.trim() !== "" || this.selectedPhase !== "all" || this.isOnlyArchived;
    },
    filteredProjectsData() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.projectsData;
      return this.projectsData.filter(row => {
        const p = row.project;
        if (!p) return false;
        const haystack = [
          p.code,
          p.title,
          ...(p.meta && p.meta.managers ? p.meta.managers.map(m => this.getFullName(m.name)) : []),
          ...(p.meta && p.meta.site_managers ? p.meta.site_managers.map(m => this.getFullName(m.name)) : [])
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(q);
      });
    }
  },
  methods: {
    phaseLabel(phase) {
      const found = this.projectPhaseOptions.find(o => o.id === phase);
      return found ? this.$t(found.translationKey) : phase;
    },
    resetFilters() {
      this.search = "";
      this.selectedPhase = "all";
      this.isOnlyArchived = false;
    },
    readProjectsData() {
      this.isLoading = true;
      this.projectsData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getProjectsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {
        action: "get_projects",
        fill_meta: true,
        filters: [['is_archived', '==', this.isOnlyArchived]]
      };
      if (this.selectedPhase !== 'all') {
        payload.filters.push(['phase', '==', this.selectedPhase]);
      }
      getProjectsFunction(payload)
          .then((result) => {
            result.data["projects"].forEach((project) => {
              this.projectsData.push({ project });
            });
            this.isLoading = false;
          })
          .catch((error) => {
            this.showError(error.message);
            console.log("Error getting projects: ", error?.code);
            this.isLoading = false;
          });
    },
    projectDetailsLink(projectId) {
      if (this.currentUserAccess("ProjectDetails", this.UserActions.VIEW, projectId))
        return "#/projects/" + projectId;
      return "#/projects/";
    },
    projectWorklogsLink(projectId) {
      return "/projects/" + projectId + "/worklogs";
    },
    projectSummaryLink(projectId) {
      return "/projects/" + projectId + "/summary";
    },
    createNewProjectClicked() {
      this.$router.push({ name: 'ProjectDetails', params: { id: "new" } });
    },
    createFromTemplateClicked() {
      this.$router.push({ name: 'Templates' });
    }
  },
  mounted() {
    if (!this.currentUserAccess("Projects", this.UserActions.VIEW)) {
      // Only bounce to the profile if we actually know who the user is; sending a
      // null id to UserDetails makes the backend choke (document_id=None).
      const userId = this.getLocalUserId();
      if (userId) this.$router.push({ name: 'UserDetails', params: { id: userId } });
      return;
    }
    this.loadUserRoleFromServer();
    this.readProjectsData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.projects {
  // OmxList provides its own surface; just give the page some breathing room.
}
.omx-link {
  color: $omx-brand-600;
  font-weight: 600;
  font-family: $omx-font-mono;
  font-size: 12.5px;
  &:hover { color: $omx-brand-700; text-decoration: underline; }
}
</style>
